import { Descriptions, DescriptionsProps, Space } from "antd";
import React, { useMemo } from "react";
import dayjs from "dayjs";
import { DATETIME_FORMAT } from "./shared";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import AutomationType from "src/consts/AutomationType";
import PremierPlusBinDTO from "src/models/PremierPlusBinDTO";
import { formatNumber } from "src/utils/formatting";
import { formatBool } from "src/pages/dashboard/BinStatusPage/HeaterControls";
import { bind } from "lodash";
import { convertFahrenheitToCelsius } from "src/utils/generalUtils";

interface OverviewProps {
    showLastTapeReadingDate: boolean;
    title?: string;
    binDto: PremierPlusBinDTO | null | undefined;
    layout: "horizontal" | "vertical"; // This defines the union type.
}

const Overview : React.FC<OverviewProps> = ({ title, layout, binDto, showLastTapeReadingDate }) => {

    const useCelsius = useMemo( () => binDto?.platformSettings?.useMetricSystem === true, [binDto]);
    
    const grainTemp = useMemo (() => {
        if(useCelsius){
            return convertFahrenheitToCelsius(binDto?.latestTapestrip?.grainTemperatureF);
        }
        return binDto?.latestTapestrip?.grainTemperatureF;
    }, [bind, useCelsius]);

    const plenumTemp = useMemo (() => {
        if(useCelsius){
            return convertFahrenheitToCelsius(binDto?.latestTapestrip?.actualPlenumTemperatureF);
        }
        return binDto?.latestTapestrip?.actualPlenumTemperatureF;
    }, [bind, useCelsius]);

    const targetTemp = useMemo (() => {
        if(useCelsius){
            return convertFahrenheitToCelsius(binDto?.latestTapestrip?.plenumTemperatureSetpointF);
        }
        return binDto?.latestTapestrip?.plenumTemperatureSetpointF;
    }, [bind, useCelsius]);


    const itemsRest: DescriptionsProps['items'] = [];

    itemsRest.push(...[
        {
            label: "Last Tape Reading",
            children: <>{binDto?.latestTapestrip?.captureTimeUtc != null ? dayjs(binDto?.latestTapestrip?.captureTimeUtc).format(DATETIME_FORMAT) : null}</>
        },
        {
            label: "Moisture (%)",
            children: formatNumber(binDto?.latestTapestrip?.individualMc, { decimalPlaces: 1}),
        },
        {
            label: "Avg. Moisture (%)",
            children: formatNumber(binDto?.latestTapestrip?.averageMc, {decimalPlaces: 1}),
        },
        {
            label: `Grain Temp ${useCelsius ? '°C' : '°F'}`,
            children: formatNumber(grainTemp, {decimalPlaces: 0}),
        },
        {
            label: `Plenum Temp ${useCelsius ? '°C' : '°F'}`,
            children: formatNumber(plenumTemp, {decimalPlaces: 0}),
        },
        {
            label: `Target Temp ${useCelsius ? '°C' : '°F'}`,
            children: formatNumber(targetTemp, { decimalPlaces: 0 }),
        },
        {
            label: "Static Pressure (InH2O)",
            children: formatNumber(binDto?.latestTapestrip?.staticPlenumPressure, { decimalPlaces: 2 }),
        },
        {
            label: "Machine",
            children: <Space direction="horizontal" wrap size="small">
                <span>{formatBool(binDto?.latestTapestrip?.isMachineOn, {
                    null: "--",
                    true: "ON",
                    false: "OFF",
                    bold: false,
                })}</span>
                {binDto?.latestTapestrip?.isMachineOn == null ? null : binDto?.latestTapestrip?.isMachineOn ? <CheckCircleFilled style={{ color: "green" }} /> : <CloseCircleFilled style={{ color: 'red' }} />}
            </Space>,
        }
    ]);

    return <>
        <Descriptions bordered layout={layout} items={itemsRest} size="small" column={1} />
    </>;
}

export default Overview;